import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Button, Spinner } from "reactstrap";
import {
  getStoreProjectEstimate,
  getStoreProjectProformaInvoice,
} from "../service";
import { fmtInvoiceRupee } from "./storeSalesDashboardHelpers";
import OrderSummaryPriceMetaRows from "../components/OrderSummaryPriceMetaRows";
import { getStoreLogoPath } from "../../utils";
import { formatBuyerBillingAddress } from "./storeDocumentHelpers";
import "./StoreProformaInvoicePrint.css";

/** Axios interceptor already returns response.data ({ success, data }). */
const unwrapDocument = (body) => body?.data ?? null;

const DetailBlock = ({ title, children }) => (
  <div>
    <div className="store-pi-print-block-title">{title}</div>
    <div>{children}</div>
  </div>
);

const StoreProformaInvoicePrint = ({ documentKind = "estimate" }) => {
  const { projectId } = useParams();
  const isProforma = documentKind === "proforma";
  const [document, setDocument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = isProforma
          ? await getStoreProjectProformaInvoice(projectId)
          : await getStoreProjectEstimate(projectId);
        if (!cancelled) {
          setDocument(unwrapDocument(response));
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            err?.response?.data?.error?.message ||
              err?.message ||
              "Failed to load document"
          );
          setDocument(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [projectId, isProforma]);

  useEffect(() => {
    if (document?.title) {
      window.document.title = document.title;
    }
  }, [document?.title]);

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100">
        <Spinner color="primary" />
      </div>
    );
  }

  if (error) {
    const isBillingError = error.includes("Billing details are missing");
    return (
      <div className="container py-5">
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
        {isBillingError ? (
          <Link to="/profile" className="btn btn-primary">
            Go to profile
          </Link>
        ) : null}
      </div>
    );
  }

  if (!document) {
    return (
      <div className="container py-5">
        <div className="alert alert-warning" role="alert">
          No document data available.
        </div>
      </div>
    );
  }

  const seller = document.seller ?? {};
  const customer = document.customer ?? {};
  const orderRef = document.order_reference ?? {};
  const priceMeta = document.price_meta ?? null;
  const payment = document.payment ?? {};
  const lineItems = Array.isArray(document.line_items)
    ? document.line_items
    : [];
  const notes = Array.isArray(document.notes) ? document.notes : [];
  const billingAddress = formatBuyerBillingAddress(customer);

  return (
    <div className="store-pi-print-root bg-light min-vh-100">
      <div className="store-pi-print-toolbar d-flex justify-content-end gap-2 p-3">
        <Button color="primary" onClick={handlePrint}>
          Print / Save as PDF
        </Button>
      </div>

      <div className="store-pi-print-page">
        <div className="store-pi-print-header d-flex justify-content-between align-items-start gap-3">
          <div>
            <img
              src={getStoreLogoPath()}
              alt="ZeroBuild"
              style={{ height: "36px" }}
            />
            <div className="store-pi-print-title mt-2">{document.title}</div>
          </div>
          <div className="text-end small">
            {isProforma && document.pi_number ? (
              <>
                <div>
                  <strong>PI No:</strong> {document.pi_number}
                </div>
                <div>
                  <strong>PI Date:</strong> {document.pi_date}
                </div>
                <div>
                  <strong>Valid Till:</strong> {document.valid_till}
                </div>
              </>
            ) : null}
          </div>
        </div>

        <div className="store-pi-print-meta-grid">
          <DetailBlock title="Seller">
            <div className="fw-semibold">{seller.name}</div>
            <div>{seller.address}</div>
            {seller.gstin ? <div>GSTIN: {seller.gstin}</div> : null}
            {seller.phone ? <div>Phone: {seller.phone}</div> : null}
            {seller.email ? <div>Email: {seller.email}</div> : null}
          </DetailBlock>

          <DetailBlock title="Customer">
            <div className="fw-semibold">{customer.name}</div>
            {customer.gst_number ? (
              <div>GSTIN: {customer.gst_number}</div>
            ) : null}
            {billingAddress ? (
              <div className="mt-1">{billingAddress}</div>
            ) : null}
          </DetailBlock>
        </div>

        <div className="store-pi-print-meta-grid mb-3">
          <DetailBlock title="Payment Terms">
            <div>
              {orderRef.payment_terms}
            </div>
          </DetailBlock>

          <DetailBlock title="Bank Details">
            <div>Bank: {payment.bank_name}</div>
            <div>A/C Name: {payment.account_name}</div>
            <div>A/C No: {payment.account_no}</div>
            <div>IFSC: {payment.ifsc}</div>
          </DetailBlock>
        </div>

        <table className="store-pi-print-table">
          <thead>
            <tr>
              <th style={{ width: "2.5rem" }}>#</th>
              <th>Item Code</th>
              <th className="text-end">Qty</th>
              <th className="text-end">Unit Price</th>
              <th className="text-end">Amount</th>
            </tr>
          </thead>
          <tbody>
            {lineItems.map((row, index) => (
              <tr key={`${row.item_code}-${index}`}>
                <td>{index + 1}</td>
                <td>{row.item_code}</td>
                <td className="text-end">{row.qty}</td>
                <td className="text-end">
                  {fmtInvoiceRupee(row.unit_price)}
                </td>
                <td className="text-end">{fmtInvoiceRupee(row.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="d-flex justify-content-end">
          <div className="store-pi-print-summary">
            <OrderSummaryPriceMetaRows meta={priceMeta} />
          </div>
        </div>

        {notes.length > 0 ? (
          <div className="store-pi-print-notes">
            <div className="store-pi-print-block-title mb-1">Notes</div>
            <ul>
              {notes.map((note) => (
                <li key={note}>{note}</li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default StoreProformaInvoicePrint;
